import React, {useRef} from 'react';
import {ReportFileFiltersType} from "../hooks/useCentralReportFileQuery";
import InputFilterItem from "../../../delivery/components/InputFilterItem";
import ParcelFilterStyle from "../../../delivery/components/ParcelFilterStyle";
import DateRangeFilterItem from "../../../delivery/components/DateRangeFilterItem";
import { useTranslation } from 'react-i18next';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';
import { getLastWeekDateRange } from '../../../../utils/dateTimeUtils';
import { useAutoClearDefaultTime } from '../../../../hooks/useAutoClearDefaultTime';
import {ReportFileTypeEnum} from '../../../../enums/ReportFileEnum';

interface CentralReportFilterProps {
    filters: ReportFileFiltersType;
    setFilters: (filters: ReportFileFiltersType) => void;
}

const CentralReportFileFilter: React.FC<CentralReportFilterProps> = ({
    filters,
    setFilters,
}) => {
    const {t} = useTranslation();
    // - undefined：未初始化，默认最近一周（仅用于 UI 默认展示）
    // - null：用户手动清空（点击叉号），保持为空
    // 一旦用户动过时间（包含点叉号清空），在 reset/刷新前都不再自动清空默认时间
    const hasUserEditedReportDateRef = useRef(false);
    useAutoClearDefaultTime({
        filters,
        setFilters,
        timeKey: 'reportDate',
        hasUserEditedTimeRef: hasUserEditedReportDateRef,
        // 页面初始化时可能会从 URL/默认值补齐 type 等非时间筛选，避免因此立刻清空默认时间
        skipFirstNonTimeChange: true,
    });

    const getEffectiveReportDate = (reportDate: any): [string | null, string | null] | null => {
        if (reportDate === null) return null;
        const isValid = reportDate &&
            Array.isArray(reportDate) &&
            reportDate.length === 2;
        return isValid ? (reportDate as [string | null, string | null]) : getLastWeekDateRange();
    };

    const reportTypeOptions = [
        { value: '', label: t('enum.reportTypeOptions.all') },
        { value: ReportFileTypeEnum.PARCEL_DOWNLOAD.toString(), label: t('enum.reportTypeOptions.parcelDownload') },
        { value: ReportFileTypeEnum.PICKUP_APPOINTMENT.toString(), label: t('enum.reportTypeOptions.pickupAppointmentDownload') },
        { value: ReportFileTypeEnum.PICKUP_PROGRESS.toString(), label: t('enum.reportTypeOptions.pickupProgressDownload') },
        { value: ReportFileTypeEnum.COOPERATION_WAREHOUSE_BLACKLIST_DOWNLOAD.toString(), label: t('enum.reportTypeOptions.cooperationWarehouseBlacklistDownload') },
        { value: ReportFileTypeEnum.PICKUP_TASK.toString(), label: t('enum.reportTypeOptions.pickupTaskDownload') },
    ];

    // 处理报表类型变化
    const handleTypeChange = (value: string) => {
        setFilters({...filters, type: value ? parseInt(value) : undefined});
    };

    // 处理操作人变化
    const handleOperatorChange = (value: string) => {
        setFilters({...filters, operatorId: value ? parseInt(value) : undefined});
    };

    // 处理报表名称变化
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilters({...filters, name: e.target.value || undefined});
    };

    // 处理报表变化
    const handleReportDateChange = (
        dates: [string | null, string | null] | null
    ) => {
        hasUserEditedReportDateRef.current = true;
        setFilters({...filters, reportDate: dates});
    };

    const col1Labels = [t('central.download.filters.reportDate')];
    const col2Labels = [t('central.download.filters.reportName')];
    const col3Labels = [t('central.download.filters.type')];
    const col1SpanClassName = getSmartLabelClassName(col1Labels);
    const col2SpanClassName = getSmartLabelClassName(col2Labels);
    const col3SpanClassName = getSmartLabelClassName(col3Labels);

    return (
        <div className="mb-2 py-2">
            {/* 使用 Grid 布局确保列对齐 - 三等分布局 */}
            <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                {/* 第一列 - 统一宽度 w-[60px]（报表日期，4个字） */}
                <div className="min-w-0">
                <DateRangeFilterItem
                    label={t('central.download.filters.reportDate')}
                    value={getEffectiveReportDate(filters.reportDate)}
                    onChange={handleReportDateChange}
                        spanClassName={col1SpanClassName}
                />
                </div>
                {/* 第二列 - 统一宽度 w-[60px]（报表名称，4个字） */}
                <div className="min-w-0">
                <InputFilterItem
                    label={t('central.download.filters.reportName')}
                    value={filters.name?.toString() || ''}
                    onChange={handleNameChange}
                        spanClassName={col2SpanClassName}
                />
                </div>
                {/* 第三列 - 统一宽度 w-[36px]（类型，2个字） */}
                <div className="min-w-0">
                <ParcelFilterStyle
                    label={t('central.download.filters.type')}
                    value={filters.type !== undefined ? filters.type.toString() : ''}
                    onChange={handleTypeChange}
                    options={reportTypeOptions}
                        spanClassName={col3SpanClassName}
                        className="w-full"
                />
                </div>
            </div>
        </div>
    );
};

export default CentralReportFileFilter;
